import CustomStore from "devextreme/data/custom_store";
import jwtService from "@service/jwt-service";
const Axios = jwtService.axiosIns;
import axios, { AxiosResponse } from "axios";

import { CommonUtil } from "@utils/CommonUtil";
import DataSource from "devextreme/data/data_source";
import { CommonConst } from "@constants/common-const";
import { checkVersion, QA_FLAG_STAGING } from "@vhows/util";
import { commonNotify } from "@utils/common";

// const approvalStatus = [
//   ["신청 완료", 0],
//   ["검토중", 1],
//   ["승인 완료", 2],
//   ["승인 거절", 3],
// ];

type CompanyResponse = {
  data: Company | CompanyV2;
};

function makeApiPath() {
  return checkVersion("0.0.10", QA_FLAG_STAGING) ? "" : checkVersion("0.0.7", QA_FLAG_STAGING) ? "/v2.2" : "";
}

//----------------------------------
// 조직 프로필
//----------------------------------

// 조직 프로필 조회
const getCompanyProfile = () => {
  return Axios.get<CompanyResponse>("/companies/profile");
};

// (전문설계 조회) 조직 프로필 조회
const getMyDesignCompanyProfile = () => {
  return Axios.get("/companies/profile/all");
};

// 조직 프로필 수정
const updateCompanyProfile = (...args: any[]) => {
  return Axios.put("/companies/profile", ...args);
};

//----------------------------------
// 조직 (운영자 기능)
//----------------------------------

// 조직 목록 조회
const getCompanies = (params: any) => {
  return Axios.get(`/companies?${params}`);
};

// 조직 목록 검색
const getCompanySearch = (params: any) => {
  return Axios.get(`/companies/search?${params}`);
};

// 조직 등록
const createCompanyCompany = (...args: any[]) => {
  return Axios.post("/companies", ...args);
};

// 조직 조회
const getCompanyCompany = (id: any) => {
  return Axios.get(`/companies${makeApiPath()}/${id}`);
};

// (전문설계 조회) 조직 조회
const getDesignCompany = (id: any) => {
  return Axios.get(`/companies/${id}/all`);
};

// 조직 수정
const updateCompanyCompany = (id: any, ...args: any[]) => {
  if (checkVersion("0.0.7", QA_FLAG_STAGING)) {
    return Axios.patch(`/companies/${id}`, ...args);
  } else {
    return Axios.put(`/companies/${id}`, ...args);
  }
};

// 조직 이미지 목록 조회
const getCompanyImages = (id: any) => {
  return Axios.get(`/companies/${id}/images`);
};

// 조직 이미지 등록
const createCompanyImages = (id: any, ...args: any[]) => {
  return Axios.post(`/companies/${id}/images`, ...args);
};

// 조직 이미지 삭제
const deleteCompanyImages = (id: any, image_id: any) => {
  return Axios.delete(`/companies/${id}/images/${image_id}`);
};

// 조직 이미지 수정
const _updateCompanyImages = (id: any, image_id: any, ...args: any) => {
  return Axios.put(`/companies/${id}/images/${image_id}`, ...args);
};

// 조직 이미지 전체수정
const _updateAllCompanyImages = (id: any, ...args: any) => {
  return Axios.put(`/companies/${id}/images/batch`, ...args);
};

// 조직 지역 일괄 업데이트
const _updateAllCompanyGeo = (...args: any) => {
  return Axios.put(`/companies/geo`, ...args);
};

// 간편상담 알림 지역 정보
const getAlarmLocations = () => {
  return Axios.get<AlarmLocations[]>("/companies/alarm/location");
};

// 공통 뱃지 목록 조회
const _getAllBadges = (params: any) => {
  return Axios.get(`/badges?${params}`);
};

// 조직 뱃지 목록 조회
const _getCompanyBadge = (id: any) => {
  return Axios.get(`/companies/${id}/badges`);
};

// 조직 뱃지 목록 수정
const _updateAllCompanyBadges = (id: any, ...args: any) => {
  return Axios.put(`/companies/${id}/badges`, ...args);
};

// 조직 유튜브 목록 조회
const _getCompanyYoutube = (id: any) => {
  return Axios.get(`/companies/${id}/youtube/videos`);
};

// 조직 유튜브 목록 수정
const _updateAllCompanyYoutubes = (id: any, ...args: any) => {
  return Axios.put(`/companies/${id}/youtube/videos`, ...args);
};

/**
 * 조직 등록/수정
 * @param accessType 접근형태
 * @param modifyType 수정형태
 * @param id 조직id
 * @param company 조직객체
 */
export async function saveCompany(
  accessType: string = "",
  modifyType: string = null,
  id: number = null,
  company: Company | CompanyV2 | CompanyV3 = null,
) {
  // store.commit("store/setLoading", true);
  // store.commit("store/setLoadMessage", "잠시만 기다려주세요."); // 저장

  // JSON화
  // const jsonData: string = TypedJSON.stringify(company, Company);
  const jsonData = JSON.stringify(company);

  try {
    if (accessType === CommonConst.ACCESS_TYPE_MY) {
      // 조직 수정
      await updateCompanyProfile(jsonData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      commonNotify(CommonConst.completeUpdate);
    } else if (accessType === CommonConst.ACCESS_TYPE_COMPANY) {
      if (modifyType === CommonConst.MODIFY_TYPE_CREATE) {
        // 조직 등록 - 조직별(운영자)
        await createCompanyCompany(jsonData, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        commonNotify(CommonConst.completeSave);
      } else {
        // 조직 수정 - 조직별(운영자)
        await updateCompanyCompany(id, jsonData, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        commonNotify(CommonConst.completeUpdate);
      }
    }
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
    // store.commit("store/setLoadMessage", "잠시만 기다려주세요.");
  }
}

/**
 * 조직 조회하기
 * @param accessType 접근형태
 * @param id 조직 id
 * @return company 조직
 */
export async function getCompany(accessType: string = "", id: number = null) {
  let company = null;
  // store.commit("store/setLoading", true);

  try {
    if (accessType === CommonConst.ACCESS_TYPE_MY) {
      // 조직 조회
      company = await getCompanyProfile();
    } else {
      // 조직 조회 - 조직별(운영자)
      company = await getCompanyCompany(id);
    }
    company = company.data;
  } catch (e) {
    console.log(e);
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
  }
  return company;
}

/**
 * 조직 목록 조회(DxDataGrid)
 */
export function getCompanyCompanyDataSource(isSearch?: boolean) {
  return new CustomStore({
    key: "id",
    onLoading() {
      // store.commit("store/setLoading", true);
    },
    onLoaded() {
      // store.commit("store/setLoading", false);
    },
    load(loadOptions) {
      if (isSearch && loadOptions.searchValue !== null) {
        loadOptions.filter = ["companyName", "contains", `${loadOptions.searchValue}`];
      }
      // 파라미터 생성
      const params = CommonUtil.makeDxDataGridParams(loadOptions);
      return getCompanies(params)
        .then((response: any) => ({
          data: response.data.data,
          totalCount: response.data.totalCount,
        }))
        .catch(() => {
          // store.commit("store/setLoading", false);
          throw new Error("Data Loading Error");
        });
    },
  });
}

/**
 * 조직리스트 검색
 */
export async function searchCompanyList(search: string = "") {
  let companyList = null;
  let data = null;
  // 통신
  try {
    // 고객 사용자 검색
    let params = `search=${search}`;
    data = await getCompanySearch(params);
    companyList = data.data;
  } catch (e) {
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  }
  return companyList;
}

/**
 * 조직 목록 검색 조회(Dx)
 */
export function getCompanySearchDataSource() {
  return new DataSource({
    paginate: false,
    store: new CustomStore({
      key: "id",
      loadMode: "processed",
      load: (loadOptions: any) => {
        let params = `search=${loadOptions.searchValue}`;
        return getCompanySearch(params).then((response: any) => response.data);
      },
      byKey: key => getCompanyCompany(key).then((response: any) => response.data),
      cacheRawData: false,
      useDefaultSearch: false,
    }),
  });
}

// 조직 사업자 등록증 다운로드
export async function downloadReNumberFile(url: string, companyName: string) {
  // store.commit("store/setLoading", true);
  let res: AxiosResponse<any, any> = null;
  try {
    res = await axios({
      method: "get",
      url,
      responseType: "blob",
    });
    const file = window.URL.createObjectURL(new Blob([res.data]));
    const allowExtension = ["pdf", "png", "jpg", "jpeg"];
    const extension = allowExtension.find(ext => res.data.type.includes(ext));

    const docUrl = document.createElement("a");
    docUrl.href = file;
    docUrl.setAttribute("download", `${companyName} 사업자 등록증.${extension}`);
    document.body.appendChild(docUrl);
    docUrl.click();
    docUrl.remove();
    window.URL.revokeObjectURL(file);
  } catch (e) {
    commonNotify(CommonConst.errorCommon, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
  }
}

/**
 * 조직 이미지 저장하기
 */
export async function saveCompanyImages(
  accessType: string = "",
  modifyType: string = null,
  id: number = null,
  showImage: CompanyImage = null,
) {
  const jsonData = JSON.stringify(showImage);

  try {
    if (showImage.id) {
      await _updateCompanyImages(id, jsonData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else {
      await createCompanyImages(id, jsonData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
  }
}

/**
 * 조직 이미지 전체 수정하기
 */
export async function updateCompanyImages(
  accessType: string = "",
  id: number = null,
  showImage: CompanyImage[] = null,
) {
  const jsonData = JSON.stringify(showImage);

  try {
    await _updateAllCompanyImages(id, jsonData, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    commonNotify(CommonConst.completeSave);
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
  }
}

/**
 * 조직별 지역 일괄 업데이트
 */
export async function updateCompanyGeo() {
  try {
    await _updateAllCompanyGeo({
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
  }
}

/**
 * 조직 이미지 조회하기
 * @param accessType 접근형태
 * @param id 조직 id
 */
export async function getCompanyImage(accessType: string = "", id: number = null) {
  let companyImage = null;
  // store.commit("store/setLoading", true);

  try {
    // 이미지 목록 조회
    companyImage = await getCompanyImages(id);
    companyImage = companyImage.data;
  } catch (e) {
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
  }
  return companyImage;
}

// 조직 사업자 등록증 다운로드
export async function downloadCompanyImageFile(url: string, companyName: string) {
  // store.commit("store/setLoading", true);
  let res: AxiosResponse<any, any> = null;
  try {
    res = await axios({
      method: "get",
      url,
      responseType: "blob",
    });
    const file = window.URL.createObjectURL(new Blob([res.data]));
    const allowExtension = ["pdf", "png", "jpg", "jpeg"];
    const extension = allowExtension.find(ext => res.data.type.includes(ext));

    const docUrl = document.createElement("a");
    docUrl.href = file;
    docUrl.setAttribute("download", `${companyName} 사업자 등록증.${extension}`);
    document.body.appendChild(docUrl);
    docUrl.click();
    docUrl.remove();
    window.URL.revokeObjectURL(file);
  } catch (e) {
    commonNotify(CommonConst.errorCommon, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
  }
}

/**
 * 업체 이미지 삭제
 */
export async function deleteImage(companyId: number, imageId: number) {
  try {
    if (imageId) {
      await deleteCompanyImages(companyId, imageId);
    }
    commonNotify(CommonConst.completeDelete);
  } catch (e: any) {
    console.log(e.response.data);
    commonNotify(CommonConst.errorDelete, "error");
    throw e;
  } finally {
  }
}

/**
 * 간편 상담 알림 지역 정보
 */
export async function getLocations() {
  try {
    const { data } = await getAlarmLocations();
    return data;
  } catch (e) {
    commonNotify(CommonConst.errorCommon, "error");
    throw e;
  }
}

/**
 * 공통 뱃지 조회하기
 */
export function getAllBadges() {
  return new CustomStore({
    key: "id",
    onLoading() {
      // store.commit("store/setLoading", true);
    },
    onLoaded() {
      // store.commit("store/setLoading", false);
    },
    async load(loadOptions) {
      // 파라미터 생성
      const params = CommonUtil.makeDxDataGridParams(loadOptions);
      try {
        let response;
        response = await _getAllBadges(params);
        return response.data;
      } catch (e) {
        // store.commit("store/setLoading", false);
        throw new Error("Data Loading Error");
      }
    },
  });
}

/**
 * 조직별 뱃지 조회하기
 * @param accessType 접근형태
 * @param id 조직 id
 */
export async function getCompanyBadge(accessType: string = "", id: number = null) {
  let companyBadge = null;
  // store.commit("store/setLoading", true);

  try {
    // 뱃지 목록 조회
    companyBadge = await _getCompanyBadge(id);
    companyBadge = companyBadge.data;
  } catch (e) {
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
  }
  return companyBadge;
}

/**
 * 조직 뱃지 수정하기
 */
export async function updateCompanyBadges(id: number = null, badges: Badges[] = null) {
  const jsonData = JSON.stringify(badges);

  try {
    await _updateAllCompanyBadges(id, jsonData, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    commonNotify(CommonConst.completeSave);
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
  }
}

/**
 * 조직별 유튜브 조회하기
 * @param accessType 접근형태
 * @param id 조직 id
 */
export async function getCompanyYoutube(accessType: string = "", id: number = null) {
  let companyYoutube = null;
  // store.commit("store/setLoading", true);

  try {
    // 유튜브 목록 조회
    companyYoutube = await _getCompanyYoutube(id);

    companyYoutube = companyYoutube.data;
  } catch (e) {
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
  }
  return companyYoutube;
}

/**
 * 조직 유튜브 수정하기
 */
export async function updateCompanyYoutubes(
  accessType: string = "",
  id: number = null,
  youtubes: YoutubeVideos[] = null,
) {
  const jsonData = JSON.stringify(youtubes);

  try {
    await _updateAllCompanyYoutubes(id, jsonData, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
  }
}
